import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router } from '@angular/router';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService } from 'app/core/user/user.service';
import { SUPER_ADMIN_ROLE } from 'app/core/user/user.types';
import { map, of, switchMap, take } from 'rxjs';

/**
 * The landing route for a given role. SuperAdmins live in the platform area;
 * everyone else (society Admin) uses the society dashboard.
 */
const homeForRole = (roleName: string | undefined): string =>
    roleName === SUPER_ADMIN_ROLE ? '/platform/societies' : '/dashboard';

export const RoleGuard: CanActivateFn | CanActivateChildFn = (route, state) => {
    const router = inject(Router);
    const authService = inject(AuthService);
    const userService = inject(UserService);

    return authService.check().pipe(
        switchMap((authenticated) => {
            if (!authenticated) {
                return of(router.parseUrl(`sign-in?redirectURL=${state.url}`));
            }

            return userService.user$.pipe(
                take(1),
                map((user) => {
                    const allowedRoles: string[] = route.data?.['roles'] || [
                        'Admin',
                    ];
                    if (user && allowedRoles.includes(user.roleName)) {
                        return true;
                    }
                    // Authenticated but wrong role for this route: send the user
                    // to their own home instead of signing them out. This keeps a
                    // SuperAdmin from being bounced when they hit a society-only
                    // route (e.g. /dashboard), and vice-versa.
                    if (user) {
                        return router.parseUrl(homeForRole(user.roleName));
                    }
                    authService.signOut();
                    return router.parseUrl('/sign-in');
                })
            );
        })
    );
};
